import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ChartsView from './sections/chart-view';
import GridView from './sections/grid-view';
import TabStripLowDom from '@components-clean/core/tabstrip-low-dom';
import { CM2Dataset } from '@interfaces/central-monitoring';
import ActionsBar from '@pages-clean/central-monitoring/components/action-bar/action-bar';
import { IFormData, StudySiteHubContext } from '@pages-clean/central-monitoring/interfaces/context';
import WidgetsSection from '@pages-clean/central-monitoring/site-hub/sections/widget-section';
import StudySiteHubWrapper, { StudySiteHubContentProps } from '@pages-clean/central-monitoring/study-site-hub-wrapper';
import { getSessionStorageSiteCode, setSessionStorageSiteCode } from '@contexts/local-state-context';
import { LoggedInPageProps } from '@config/routes/interfaces';

import './index.scss';

const Content = ({ studyRef, dataset, studyDashboardConfig }: StudySiteHubContentProps) => {
    const [siteId, setSiteId] = useState<number>();
    const [siteName, setSiteName] = useState<string>();
    const [comparisonDataset, setComparisonDataset] = useState<CM2Dataset>();
    const params = new URLSearchParams(window.location.search);

    const siteCode: string | undefined = params.get('sitecode') ?? undefined;
    useEffect(() => {
        if (siteCode) {
            // If provided with a site code via query params, make sure to set it to the session storage too
            setSessionStorageSiteCode(siteCode);
        }
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, []);

    const sessionData = getSessionStorageSiteCode() ?? undefined;

    const [formData, setFormData] = useState<IFormData>({
        studyRef,
        dataset,
        hidePanelSetup: true,
        defaultDatasetFilters: {
            dataSetFilter: studyDashboardConfig.studyDashboardConfigForStudy?.dataSetFilter,
            dataSetsCount: studyDashboardConfig?.studyDashboardConfigForStudy?.dataSetsCount,
            comparisonDataset: null,
        },
        datasetFilters: {
            dataSetFilter: studyDashboardConfig.studyDashboardConfigForStudy?.dataSetFilter,
            dataSetsCount: studyDashboardConfig?.studyDashboardConfigForStudy?.dataSetsCount,
            comparisonDataset,
        },
        siteId: 0,
        siteCode: siteCode ?? sessionData,
        siteName: '',
    });

    const siteHubProviderValue = useMemo(
        () => ({
            current: formData,
            setCurrent: setFormData,
            setSiteId,
            setSiteName,
            setComparisonDataset,
        }),
        [formData, setFormData],
    );

    const { t } = useTranslation('cm', { keyPrefix: 'siteHubPage' });

    useEffect(() => {
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/set-state-in-effect
        setFormData({
            ...formData,
            studyRef,
            dataset,
            siteId,
            siteName,
            datasetFilters: {
                ...formData.datasetFilters,
                comparisonDataset,
            },
        });
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [studyRef, dataset, siteId, comparisonDataset]);

    return (
        <StudySiteHubContext value={siteHubProviderValue}>
            <div className="site-hub">
                <ActionsBar studyRef={studyRef} datasetId={dataset.id} datasetSelector siteSelector />

                <WidgetsSection />

                <TabStripLowDom
                    data={[
                        {
                            title: t('chartViewTab'),
                            content: <ChartsView />,
                            dataTestId: 'sitehub-chart-tab',
                        },
                        {
                            title: t('gridTab'),
                            content: <GridView />,
                            dataTestId: 'sitehub-grid-tab',
                        },
                    ]}
                />
            </div>
        </StudySiteHubContext>
    );
};

const SiteHub = ({ study, cm2Dataset }: LoggedInPageProps) => {
    return <StudySiteHubWrapper study={study} cm2Dataset={cm2Dataset} Content={Content} />;
};

export default SiteHub;
